<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'

// Renders a structured cell's scalar leaves the way the Anonymization widget
// renders providers: the first leaf inline as plain text, the rest behind a
// "+N" dropdown. Each leaf (inline and in the dropdown) carries
// data-shift-field / data-shift-value so the host's cell-selection path can
// select that single value (see GenericSelectorTable.resolveGenericFromEvent /
// IpResultsTable.resolveCellFromEvent). Per-leaf +/- filtering lives in the
// dropdown; the whole-cell +/- (the host's hover buttons) filters the value as a
// whole. Empty structured cells render blank.
//
// The dropdown's filter buttons stop mousedown/click propagation so they never
// trigger the host cell's select handler.

const props = defineProps({
  // Scalar leaves of the cell value, already flattened by the host.
  leaves: {
    type: Array,
    default: () => [],
  },
  // Column/field id, stamped onto each leaf as data-shift-field so a click
  // resolves to `field:value` for that single leaf.
  field: {
    type: String,
    default: '',
  },
  // The cell has no filterable content (e.g. {}, [{}], []) — renders blank.
  empty: {
    type: Boolean,
    default: false,
  },
  // How many leaves to show inline before collapsing the rest into "+N". Mirrors
  // the Anonymization widget: one value inline, the rest behind the "+N" dropdown.
  max: {
    type: Number,
    default: 1,
  },
  // Show the +/- filter affordances in the dropdown.
  filters: {
    type: Boolean,
    default: false,
  },
})

// emit('filter', { value, empty }, negate)
const emit = defineEmits(['filter'])

const inlineLeaves = computed(() => props.leaves.slice(0, props.max))
const overflowCount = computed(() => Math.max(0, props.leaves.length - props.max))
const hasOverflow = computed(() => overflowCount.value > 0)

const open = ref(false)
const triggerRef = ref(null)
const menuRef = ref(null)
const style = ref({})

async function toggleMenu() {
  if (open.value) {
    open.value = false
    return
  }
  open.value = true
  await nextTick()
  position()
}

function position() {
  if (!triggerRef.value || !menuRef.value) return
  const anchor = triggerRef.value.getBoundingClientRect()
  const rect = menuRef.value.getBoundingClientRect()
  const vw = window.innerWidth
  const vh = window.innerHeight
  let left = anchor.left
  let top = anchor.bottom + 4
  if (left + rect.width > vw - 8) left = Math.max(8, vw - rect.width - 8)
  if (top + rect.height > vh - 8) top = Math.max(8, anchor.top - rect.height - 4)
  style.value = { top: `${top}px`, left: `${left}px` }
}

function emitFilter(leaf, negate) {
  emit('filter', { value: leaf, empty: false }, negate)
  open.value = false
}

function onKeydown(event) {
  if (event.key === 'Escape' && open.value) open.value = false
}

function onClickOutside(event) {
  if (!open.value) return
  if (menuRef.value?.contains(event.target)) return
  if (triggerRef.value?.contains(event.target)) return
  open.value = false
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
  document.addEventListener('click', onClickOutside, true)
  window.addEventListener('resize', position)
  window.addEventListener('scroll', position, true)
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
  document.removeEventListener('click', onClickOutside, true)
  window.removeEventListener('resize', position)
  window.removeEventListener('scroll', position, true)
})
</script>

<template>
  <div class="cvc-cell">
    <template v-if="!empty">
      <span
        v-for="(leaf, i) in inlineLeaves"
        :key="i"
        class="cvc-name"
        :data-shift-field="field"
        :data-shift-value="String(leaf)"
        :title="String(leaf)"
      >{{ leaf }}</span>

      <button
        v-if="hasOverflow"
        ref="triggerRef"
        class="cvc-more"
        :title="`Show all ${leaves.length} values`"
        @mousedown.stop
        @click.stop="toggleMenu"
      >
        +{{ overflowCount }}
      </button>
    </template>

    <Teleport to="body">
      <div v-if="open" ref="menuRef" class="cvc-menu" :style="style">
        <div
          v-for="(leaf, i) in leaves"
          :key="i"
          class="cvc-menu-row"
          :data-shift-field="field"
          :data-shift-value="String(leaf)"
        >
          <span class="cvc-menu-name" :title="String(leaf)">{{ leaf }}</span>
          <span v-if="filters" class="cvc-actions">
            <button
              class="cvc-btn cvc-for"
              title="Filter for value"
              @mousedown.stop
              @click.stop="emitFilter(leaf, false)"
            >
              <font-awesome-icon :icon="['fas', 'plus']" />
            </button>
            <button
              class="cvc-btn cvc-out"
              title="Filter out value"
              @mousedown.stop
              @click.stop="emitFilter(leaf, true)"
            >
              <font-awesome-icon :icon="['fas', 'minus']" />
            </button>
          </span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
/* Mirrors the Anonymization widget (Query/Widget/Anonymization.vue). */
.cvc-cell {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
}

.cvc-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cvc-more {
  flex-shrink: 0;
  padding: 0 0.35rem;
  font-size: 0.72rem;
  color: var(--text-muted);
  background: transparent;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xs);
  cursor: pointer;
}

.cvc-more:hover {
  color: var(--accent-text);
  border-color: rgba(115, 87, 210, 0.4);
  background: var(--accent-soft-3);
}

.cvc-menu {
  position: fixed;
  z-index: 1000;
  min-width: 12rem;
  max-width: 22rem;
  max-height: 18rem;
  overflow-y: auto;
  padding: 0.25rem;
  background: var(--surface-page);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg, 0 10px 40px rgba(0, 0, 0, 0.4));
}

.cvc-menu-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.4rem;
  border-radius: var(--radius-sm);
}

.cvc-menu-row:hover {
  background: var(--surface-hover);
}

.cvc-menu-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.8rem;
  color: var(--text-primary);
}

.cvc-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity var(--transition-fast, 0.15s) ease;
}

.cvc-menu-row:hover .cvc-actions {
  opacity: 1;
}

.cvc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  padding: 0;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xs);
  background: var(--surface-card);
  color: var(--text-faint);
  cursor: pointer;
  font-size: 0.5rem;
  transition: all var(--transition-fast, 0.15s) ease;
}

.cvc-for:hover {
  background: var(--accent-soft-3);
  border-color: rgba(115, 87, 210, 0.4);
  color: var(--accent-text);
}

.cvc-out:hover {
  background: var(--color-error-soft, rgba(239, 68, 68, 0.12));
  border-color: rgba(239, 68, 68, 0.4);
  color: var(--color-error);
}
</style>
